import { useQuery } from '@tanstack/react-query';
import getFormVariantValues from '../lib/getFormVariantValues';
import useCheckoutState from './useCheckoutState';
import api from '../api';
import { prorationCalculationQueryKey } from '../queryKeys';
import { checkHasBillingCycleAnchorConfig } from '../lib/checkHasBillingCycleAnchorConfig';
import getPriceBillingConfig from '../lib/getPriceBillingConfig';
import useSelectedPrice from './useSelectedPrice';
import checkIsCoverProcessingFeeSelected from '../lib/checkCoverProcessingFee';

export function useCalculateProratedAmountQuery(
  {
    sellerId,
    pageId,
    variants,
    quantity,
    selectedPaymentMethod,
    couponId,
    dynamicPrice,
    priceId,
    billingCycleAnchorConfigEnabled,
    dayOfMonth,
    month,
    coverProcessingFee = false,
  },
  enabled,
  { initialData },
) {
  const queryKey = prorationCalculationQueryKey({
    sellerId,
    pageId,
    variants,
    quantity,
    selectedPaymentMethod,
    dynamicPrice,
    priceId,
    couponId,
    billingCycleAnchorConfigEnabled,
    dayOfMonth,
    month,
  });

  return useQuery({
    queryKey,
    queryFn: () =>
      api.calculateProration(
        {
          variants,
          quantity,
          selectedPaymentMethod,
          couponId,
          priceId,
          // Send the anchor the caller is keyed on so the server computes the
          // date from the request, not from the (possibly not-yet-saved) DB
          // price. This keeps the result a pure function of the query key and
          // removes the dashboard preview's save-vs-fetch race.
          billingCycleAnchorConfigEnabled,
          dayOfMonth,
          month,
          // Not in the query key: the contribution is added on top of the prorated amount.
          coverProcessingFee,
        },
        sellerId,
        pageId,
      ),
    enabled: enabled && !!pageId && !!sellerId,
    retry: 3,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    refetchInterval: false,
    ...(initialData && { initialData }),
  });
}

export default function useProration(priceOverride) {
  const {
    page,
    seller,
    form,
    product,
    coupon,
    selectedPaymentMethod,
    dynamicPrice,
    effectiveSelectedPriceId,
    paymentOption,
  } = useCheckoutState();
  const selectedPrice = useSelectedPrice();

  if (!product) {
    return {
      proration: {
        amount: 0,
        nextBillingDate: null,
      },
      isLoadingProration: false,
      isRefetchingProration: false,
      hasProration: false,
    };
  }

  // Per-option mode: when a price is passed, compute for THAT price regardless
  // of selection — the picker shows every option's proration up front, on load.
  // Otherwise resolve the billing-cycle anchor from the selected price for
  // multi-price products (each price carries its own anchor + interval) and
  // fall back to the product-level config for legacy single-price products.
  // The proration API re-resolves from priceId server-side either way.
  // TODO(M14): legacy product-scalar fallback — remove once the prices[] backfill migration has run (product.billingCycleAnchorConfig)
  const anchorSource = priceOverride ?? (product.prices?.length > 0 ? selectedPrice : product);
  const targetPriceId = priceOverride ? String(priceOverride._id) : effectiveSelectedPriceId;

  const hasBillingCycleAnchorConfig =
    !!anchorSource && checkHasBillingCycleAnchorConfig(anchorSource);
  const hasProration = hasBillingCycleAnchorConfig; // proration calculations are already included in tax calculations

  const anchorConfig = getPriceBillingConfig(anchorSource)?.billingCycleAnchorConfig;
  const billingCycleAnchorConfigEnabled = anchorConfig?.enabled;
  const dayOfMonth = anchorConfig?.dayOfMonth;
  const month = anchorConfig?.month;

  // Variants scoped to the target price, so an option's "Today …" reflects
  // what the cart would be on that price (selections for other prices don't
  // leak in; the query refetches via its key once real selections change).
  const variants = getFormVariantValues(product.variants, form.variants, undefined, targetPriceId);

  const { data, isLoading, isRefetching } = useCalculateProratedAmountQuery(
    {
      sellerId: seller?._id,
      pageId: page?._id,
      variants,
      quantity: form.quantity,
      selectedPaymentMethod,
      couponId: coupon?._id,
      dynamicPrice,
      priceId: targetPriceId,
      billingCycleAnchorConfigEnabled,
      dayOfMonth,
      month,
      coverProcessingFee: checkIsCoverProcessingFeeSelected({ page, form, paymentOption }),
    },
    hasProration,
    {
      initialData: priceOverride ? undefined : product.proration,
    },
  );

  return {
    proration: {
      amount: data?.amount || 0,
      nextBillingDate: data?.nextBillingDate,
      fullPrice: !!data?.fullPrice,
    },
    isLoadingProration: isLoading,
    isRefetchingProration: isRefetching,
    hasProration,
  };
}
